import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment';
import { finalize, map } from 'rxjs/operators';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import {
  DashboardDateRange,
  DashboardFilters,
  DashboardPeriod,
  DashboardScope,
  DEFAULT_DASHBOARD_FILTERS,
  IsoDate,
} from '../../domain/model/dashboard-filters.model';
import { DashboardCustomDateDialogComponent } from '../dashboard-custom-date-dialog/dashboard-custom-date-dialog.component';
import { DashboardCustomDateDialogResult } from '../dashboard-custom-date-dialog/dashboard-custom-date-dialog.model';
import { companyToday, formatDateRange, formatFreshnessLabel } from '../dashboard-filters.helper';

export interface DashboardFilterBarVm {
  showPeriod: boolean;
  period: DashboardPeriod;
  scope: DashboardScope;
  /** `Updated <relative> · <from> – <to>`, the range only while the period is shown; empty when no part is. */
  label: string;
}

export interface DashboardToggleOption<T> {
  value: T;
  label: string;
}

const PERIOD_OPTIONS: DashboardToggleOption<DashboardPeriod>[] = [
  { value: DashboardPeriod.MTD, label: 'MTD' },
  { value: DashboardPeriod.QTD, label: 'QTD' },
  { value: DashboardPeriod.YTD, label: 'YTD' },
];

const SCOPE_OPTIONS: DashboardToggleOption<DashboardScope>[] = [
  { value: DashboardScope.MY_DEALS, label: 'My deals' },
  { value: DashboardScope.WHOLE_TEAM, label: 'Whole team' },
];

/**
 * Shared dashboard filter bar: period + scope toggles and the freshness label. Emits into
 * `DashboardFiltersFacade` only — no HTTP, no page knowledge. The host supplies `asOfUtc` from its own data, and
 * `showPeriod: false` for a page the period does not apply to.
 */
@UntilDestroy()
@Component({
  selector: 'ifbp-dashboard-filter-bar',
  templateUrl: './dashboard-filter-bar.component.html',
  styleUrls: ['./dashboard-filter-bar.component.scss'],
  standalone: false,
})
export class DashboardFilterBarComponent implements OnInit {
  @Input() set asOfUtc(value: Date | null) {
    this.asOfUtc$.next(value);
  }

  /** Off drops the period segments and the range; the stored period is left as it is. */
  @Input() set showPeriod(value: boolean | undefined) {
    this.showPeriod$.next(value ?? true);
  }

  @ViewChild('periodGroup') periodGroup: MatButtonToggleGroup;
  @ViewChildren('periodToggle', { read: MatButtonToggle }) periodToggles: QueryList<MatButtonToggle>;

  vm$: Observable<DashboardFilterBarVm>;
  readonly periodOptions = PERIOD_OPTIONS;
  readonly scopeOptions = SCOPE_OPTIONS;
  readonly customPeriod = DashboardPeriod.CUSTOM;

  private readonly asOfUtc$ = new BehaviorSubject<Date | null>(null);
  private readonly showPeriod$ = new BehaviorSubject<boolean>(true);
  private filters: DashboardFilters = DEFAULT_DASHBOARD_FILTERS;
  private today: IsoDate = companyToday(new Date());
  private customDialogOpen = false;

  constructor(
    private readonly facade: DashboardFiltersFacade,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.vm$ = combineLatest([
      this.facade.filters$,
      this.facade.dateRange$,
      this.facade.now$,
      this.asOfUtc$,
      this.showPeriod$,
    ]).pipe(
      map(
        ([filters, range, now, asOfUtc, showPeriod]: [
          DashboardFilters,
          DashboardDateRange | null,
          Date,
          Date | null,
          boolean,
        ]) => this.buildVm(filters, range, now, asOfUtc, showPeriod),
      ),
    );

    // What the custom date dialog opens with.
    combineLatest([this.facade.filters$, this.facade.now$])
      .pipe(untilDestroyed(this))
      .subscribe(([filters, now]: [DashboardFilters, Date]) => {
        this.filters = filters;
        this.today = companyToday(now);
      });
  }

  onPeriodChange(event: MatButtonToggleChange): void {
    const period = event.value as DashboardPeriod;
    if (period === this.customPeriod) {
      // The group checks the clicked segment itself; only a submitted range may move the selection.
      this.periodGroup.value = this.filters.period;
      this.openCustomDialog();
      return;
    }
    this.facade.setPeriod(period);
  }

  /**
   * The segment's own `change`, which fires on every activation — the only signal an already-selected Custom
   * sends, since the group suppresses a `change` that flips no checked state and its keyboard handler calls
   * `preventDefault` before activating. It is also the last step of Material's click handler, so the tab stop
   * is repaired here, after that handler has moved it.
   */
  onCustomPeriodActivate(): void {
    this.syncTabStop();
    this.openCustomDialog();
  }

  /** An unselected Custom reaches this twice — group `change` and segment `change`; one dialog answers both. */
  private openCustomDialog(): void {
    if (this.customDialogOpen) {
      return;
    }
    // Latched only once the dialog exists, so a throw out of `open` leaves nothing to unlatch; `finalize`
    // covers every other way the stream can end, including teardown without an emission.
    const closed$ = DashboardCustomDateDialogComponent.show(this.dialog, {
      from: this.filters.customFrom,
      to: this.filters.customTo,
      today: this.today,
    });
    this.customDialogOpen = true;
    closed$
      .pipe(
        finalize(() => {
          this.customDialogOpen = false;
        }),
        untilDestroyed(this),
      )
      .subscribe((result?: DashboardCustomDateDialogResult) => {
        if (result) {
          // The dialog never closes with an invalid range, so the rejection this returns is unreachable.
          void this.facade.setCustomRange(result.from, result.to);
        }
      });
  }

  onScopeChange(event: MatButtonToggleChange): void {
    this.facade.setScope(event.value as DashboardScope);
  }

  trackByValue<T>(_index: number, option: DashboardToggleOption<T>): T {
    return option.value;
  }

  /** Material leaves the group's single tab stop on the clicked segment; the revert needs it on the checked one. */
  private syncTabStop(): void {
    this.periodToggles.forEach((toggle: MatButtonToggle) => (toggle.tabIndex = toggle.checked ? 0 : -1));
  }

  private buildVm(
    filters: DashboardFilters,
    range: DashboardDateRange | null,
    now: Date,
    asOfUtc: Date | null,
    showPeriod: boolean,
  ): DashboardFilterBarVm {
    const updated = asOfUtc ? `Updated ${moment(asOfUtc).from(now)}` : null;
    return {
      showPeriod,
      period: filters.period,
      scope: filters.scope,
      label: formatFreshnessLabel(updated, showPeriod ? formatDateRange(range) : null),
    };
  }
}
